import styled from 'styled-components';
import { gql } from '@apollo/client';
import { NextPage } from 'next';
import { FOOTER_QUERY } from '../components/footer';
import { HEADER_QUERY, Header } from '../components/header';
import { ContentStackClient } from '../lib/contentstack';
import { Platform } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { Footer } from '../components/footer';
import { mediaScreen } from '../utils/mediaScreen';
import { getAssetUrl } from '../utils/contentstack';
import { Col, ResponsiveContainer, Row } from '../components/responsive';
import Link from 'next/link';
import { Button } from '../components/ui/button';
import { Accordion } from '../components/accordion';
import Lottie from 'react-lottie';
import { useWindowWidth } from '@react-hook/window-size/throttled';
import { getTheme } from '../theme/getTheme';
import { useEffect, useState } from 'react';
import {
  SectionEyebrow,
  SectionHeadline,
  SectionSubheadline,
} from '../components/section';

export const PlatformPage: NextPage<
  IPageProps<Platform> & {
    howItWorksAnimation: {
      desktop: object;
      mobile: object;
    };
  }
> = ({ header, page, footer, howItWorksAnimation }) => {
  const theme = getTheme();
  const windowWidth = useWindowWidth();
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    setIsDesktop(windowWidth >= theme.breakpoints.md);
  }, [windowWidth, theme.breakpoints.md]);

  return (
    <>
      <Header
        pageTitle={page.title}
        pageDescription={page.description}
        {...header}
      />

      <Hero>
        <SectionEyebrow>{page?.hero?.eyebrow}</SectionEyebrow>
        <SectionHeadline>{page?.hero?.headline}</SectionHeadline>
        <SectionSubheadline>{page?.hero?.subheadline}</SectionSubheadline>

        <img
          className="image"
          src={getAssetUrl(page?.hero?.imageConnection)}
          alt=""
        />

        <div className="buttons">
          {page?.hero?.primary_button && (
            <Link
              href={page?.hero?.primary_button?.href!}
              legacyBehavior
              passHref
            >
              <Button as="a" mType="primary">
                {page?.hero?.primary_button?.title}
              </Button>
            </Link>
          )}

          {page?.hero?.secondary_button && (
            <Link
              href={page?.hero?.secondary_button?.href!}
              legacyBehavior
              passHref
            >
              <Button as="a" mType="secondary">
                {page?.hero?.secondary_button?.title}
              </Button>
            </Link>
          )}
        </div>
      </Hero>

      <Section1 id="how-it-works">
        <Row>
          <Col lg={4} className="texts">
            <SectionEyebrow>{page?.section_1?.eyebrow}</SectionEyebrow>
            <SectionHeadline>{page?.section_1?.headline}</SectionHeadline>
            <SectionSubheadline>
              {page?.section_1?.subheadline}
            </SectionSubheadline>
          </Col>

          <Col lg={8}>
            {!!isDesktop && (
              <div className="animation-desktop">
                <Lottie
                  isClickToPauseDisabled={true}
                  options={{
                    animationData: howItWorksAnimation.desktop,
                    autoplay: true,
                    loop: true,
                  }}
                />
              </div>
            )}

            {!isDesktop && (
              <div className="animation-mobile">
                <Lottie
                  isClickToPauseDisabled={true}
                  options={{
                    animationData: howItWorksAnimation.mobile,
                    autoplay: true,
                    loop: true,
                  }}
                />
              </div>
            )}
          </Col>
        </Row>
      </Section1>

      <Section2 id="what-you-get">
        <ResponsiveContainer>
          <Row>
            <Col xs={12} md={6} className="texts">
              <SectionEyebrow invert>{page?.section_2?.eyebrow}</SectionEyebrow>
              <SectionHeadline invert>
                {page?.section_2?.headline}
              </SectionHeadline>

              <Accordion
                invert={true}
                items={page?.section_2?.accordion_items?.map((item) => ({
                  title: item?.title!,
                  content: item?.content!,
                }))}
              />
            </Col>

            <Col xs={12} md={6} className="image">
              <img src={getAssetUrl(page?.section_2?.imageConnection)} alt="" />
            </Col>
          </Row>
        </ResponsiveContainer>
      </Section2>

      <Section3 id="what-we-serve">
        <ResponsiveContainer>
          <Row>
            <Col xs={12} md={4} className="texts">
              <SectionEyebrow>{page?.section_3?.eyebrow}</SectionEyebrow>
              <SectionHeadline>{page?.section_3?.headline}</SectionHeadline>
              <SectionSubheadline>
                {page?.section_3?.subheadline}
              </SectionSubheadline>
            </Col>

            <Col xs={12} md={1} />

            <Col xs={12} md={7}>
              <Accordion
                items={page?.section_3?.accordion_items?.map((item) => ({
                  icon: getAssetUrl(item?.iconConnection),
                  title: item?.title!,
                  content: item?.content!,
                  links: item?.links!,
                }))}
              />
            </Col>
          </Row>
        </ResponsiveContainer>
      </Section3>

      <Section4 id="outcomes">
        <ResponsiveContainer>
          <Row>
            <Col xs={12} md={7} className="image">
              <img src={getAssetUrl(page?.section_4?.imageConnection)} alt="" />
            </Col>

            <Col xs={12} md={5} className="texts">
              <SectionEyebrow invert>{page?.section_4?.eyebrow}</SectionEyebrow>
              <SectionHeadline invert>
                {page?.section_4?.headline}
              </SectionHeadline>
              <SectionSubheadline
                invert
                dangerouslySetInnerHTML={{
                  __html: page?.section_4?.subheadline!,
                }}
              />

              <div className="buttons">
                {!!page?.section_4?.primary_button && (
                  <Link
                    href={page?.section_4?.primary_button?.href!}
                    legacyBehavior
                    passHref
                  >
                    <Button as="a" mType="primary">
                      {page?.section_4?.primary_button?.title}
                    </Button>
                  </Link>
                )}
              </div>
            </Col>
          </Row>
        </ResponsiveContainer>
      </Section4>

      <Section5 id="specs">
        {page?.section_5?.cards?.map((card) => (
          <div key={card?.eyebrow} className="card">
            <img src="/img/byteflow.jpg" className="image" alt="" />
            <div className="overlay" />

            <div className="texts">
              <SectionEyebrow>{card?.eyebrow}</SectionEyebrow>
              <SectionHeadline>{card?.headline}</SectionHeadline>
              <SectionSubheadline
                dangerouslySetInnerHTML={{ __html: card?.subheadline! }}
              />

              {card?.primary_button?.title && (
                <Link
                  href={card?.primary_button?.href!}
                  legacyBehavior
                  passHref
                >
                  <Button as="a" mType="primary" className="mButton">
                    {card?.primary_button?.title}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        ))}
      </Section5>

      <Section6 id="stats">
        <SectionEyebrow>{page?.section_6?.eyebrow}</SectionEyebrow>
        <SectionHeadline>{page?.section_6?.headline}</SectionHeadline>
        <SectionSubheadline>{page?.section_6?.subheadline}</SectionSubheadline>

        <Row>
          {page?.section_6?.cards?.map((card) => (
            <Col key={card?.title} xs={6} md={3}>
              <div className="card">
                <img
                  className="icon"
                  src={getAssetUrl(card?.iconConnection)}
                  alt=""
                />
                <h4
                  className="title"
                  dangerouslySetInnerHTML={{ __html: card?.title!! }}
                />

                <p
                  className="description"
                  dangerouslySetInnerHTML={{ __html: card?.description!! }}
                />
              </div>
            </Col>
          ))}
        </Row>

        <p
          className="supporting-text"
          dangerouslySetInnerHTML={{
            __html: page?.section_6?.supporting_text!,
          }}
        />
      </Section6>

      <Section7 id="faq">
        <ResponsiveContainer>
          <SectionEyebrow invert>{page?.section_7?.eyebrow}</SectionEyebrow>
          <SectionHeadline invert>{page?.section_7?.headline}</SectionHeadline>

          <Accordion
            invert
            items={page?.section_7?.accordion_items?.map((item) => ({
              title: item?.title!,
              content: item?.content!,
            }))}
          />

          {page?.section_7?.primary_button && (
            <Link
              href={page?.section_7?.primary_button?.href!}
              legacyBehavior
              passHref
            >
              <Button as="a" mType="secondary" invert>
                {page?.section_7?.primary_button?.title}
              </Button>
            </Link>
          )}
        </ResponsiveContainer>
      </Section7>

      <Footer marginTop={false} sections={footer?.sections} {...page?.footer} />
    </>
  );
};

export async function getServerSideProps() {
  try {
    const [{ data: data }] = await Promise.all([
      ContentStackClient().query<{ page: { data: Platform[] } }>({
        query: PAGE_QUERY,
      }),
    ]);

    const page = data?.page?.data?.[0];

    const howItWorksAnimation = { desktop: {}, mobile: {} };

    try {
      howItWorksAnimation.desktop = await fetch(
        getAssetUrl(page?.section_1?.animation_lottie_file?.desktopConnection)
      ).then((res) => res.json());

      howItWorksAnimation.mobile = await fetch(
        getAssetUrl(page?.section_1?.animation_lottie_file?.mobileConnection)
      ).then((res) => res.json());
    } catch (e) {}

    return {
      props: {
        ...data,
        howItWorksAnimation,
        page,
      },
    };
  } catch (e: any) {
    console.log(JSON.stringify(e?.networkError?.result, null, 2));
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query homeQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: all_platform {
      data: items {
        title
        description
        hero {
          eyebrow
          headline
          subheadline
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
          imageConnection {
            edges {
              node {
                url
              }
            }
          }
        }
        section_1 {
          eyebrow
          headline
          subheadline
          animation_lottie_file {
            desktopConnection {
              edges {
                node {
                  url
                }
              }
            }
            mobileConnection {
              edges {
                node {
                  url
                }
              }
            }
          }
        }
        section_2 {
          eyebrow
          headline
          imageConnection {
            edges {
              node {
                url
              }
            }
          }
          accordion_items {
            title
            content
          }
        }
        section_3 {
          eyebrow
          headline
          subheadline
          accordion_items {
            title
            content
            iconConnection {
              edges {
                node {
                  url
                }
              }
            }
            links {
              title
              href
            }
          }
        }
        section_4 {
          eyebrow
          headline
          subheadline
          imageConnection {
            edges {
              node {
                url
              }
            }
          }
          primary_button {
            title
            href
          }
        }
        section_5 {
          cards {
            eyebrow
            headline
            subheadline
            primary_button {
              title
              href
            }
          }
        }
        section_6 {
          eyebrow
          headline
          subheadline
          supporting_text
          cards {
            title
            description
            iconConnection {
              edges {
                node {
                  url
                }
              }
            }
          }
        }
        section_7 {
          eyebrow
          headline
          accordion_items {
            title
            content
          }
          primary_button {
            title
            href
          }
        }
        footer {
          headline
          subheadline
          primary_button {
            title
            href
          }
          secondary_button {
            title
            href
          }
          third_button {
            title
            href
          }
        }
      }
    }
  }
`;

const Hero = styled(ResponsiveContainer)`
  padding-top: 140px;
  padding-bottom: 80px;
  text-align: center;

  > * {
    width: 100%;
    max-width: 100% !important;
    text-align: center;
  }

  .image {
    margin: 40px auto;
  }

  .buttons {
    display: flex;
    justify-content: center;
    gap: 24px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 100px;
    padding-bottom: 60px;

    .image {
      max-width: 100%;
    }

    .buttons {
      flex-direction: column;
      gap: 16px;
    }
  }
`;

const Section1 = styled(ResponsiveContainer)`
  padding-top: 100px;
  padding-bottom: 60px;

  .texts {
    padding-right: 40px;
  }

  .animation-desktop {
    position: relative;
    width: 110% !important;
    top: -70px !important;

    -webkit-mask-image: linear-gradient(
      to bottom,
      transparent 0,
      black 25%,
      black 75%,
      transparent 100%
    );
    mask-image: linear-gradient(
      to bottom,
      transparent 0,
      black 25%,
      black 75%,
      transparent 100%
    );
  }

  .animation-mobile {
    margin-top: -70px;
    margin-bottom: -80px;
    position: relative;
    width: 200% !important;
    margin-left: -50% !important;
  }

  ${mediaScreen('lg', 'xl')} {
    ${SectionHeadline} {
      font-size: 44px;
    }

    .animation-desktop {
      top: -40px !important;
    }
  }

  ${mediaScreen('xs', 'lg')} {
    padding-top: 0;
    padding-bottom: 30px;

    ${SectionHeadline}, ${SectionSubheadline} {
      max-width: 100%;
    }

    .texts {
      padding: 0;
    }

    .animation-desktop {
      top: 70px !important;
      margin-bottom: 100px;
    }
  }
`;

const Section2 = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;
  background: white;

  .row {
    gap: 80px;
    flex-wrap: nowrap;
  }

  .image {
    display: flex;
    align-items: center;
  }

  .accordion {
    margin-top: 40px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 30px;
    padding-bottom: 60px;

    .row {
      gap: 0;
      flex-wrap: wrap;
    }

    .image {
      order: -1;
      margin-bottom: 40px;
    }
  }
`;

const Section3 = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;
  background-image: url('/img/building-lines.svg');
  background-repeat: no-repeat;
  background-position: bottom left;
  background-size: 60%;

  .texts {
    > * {
      max-width: 310px;
    }
  }
  .accordion {
    margin-top: 30px;
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 30px;
    padding-bottom: 40px;
    background-position: left -17%;
    background-repeat: no-repeat;
    background-size: 100%;
  }
`;

const Section4 = styled.section`
  padding-top: 120px;
  padding-bottom: 120px;
  background-color: white;
  background-image: url('/img/dots-bg.svg');
  background-repeat: no-repeat;
  background-position: bottom left;
  background-size: 100%;

  .image {
    display: flex;

    img {
      max-width: 80%;
    }
  }

  .texts {
    ul {
      margin: 0;
      padding: 0;
      padding-left: 15px;

      li {
        margin-bottom: 8px;

        &::marker {
          color: ${(p) => p.theme.colors.teal};
        }
      }
    }

    > * {
      max-width: 400px;
    }

    ${SectionSubheadline} {
      margin-bottom: 40px;
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 60px;
    padding-bottom: 40px;
    background-image: none;

    .image {
      margin-bottom: 30px;

      img {
        max-width: 100%;
        width: 100%;
      }
    }

    .texts {
      > * {
        max-width: 100%;
        width: 100%;
        display: block;
      }

      .mButton {
        text-align: center;
        display: flex;
      }
    }
  }
`;

const Section5 = styled(ResponsiveContainer)`
  padding-top: 100px;
  display: flex;
  justify-content: center;
  gap: 30px;

  .card {
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    flex: 1;
    max-width: 588px;

    .image {
      position: absolute;
      max-width: unset;
      width: 1600px;
      left: -170px;
      pointer-events: none;
      opacity: 0.4;
    }

    &:last-of-type {
      .image {
        left: -790px;
      }
    }

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #191f2ccc;
      pointer-events: none;
    }

    .texts {
      position: relative;
      z-index: 2;
      padding: 32px 44px;
      max-width: 500px;

      ul {
        margin: 0;
        padding-left: 15px;

        li {
          margin-bottom: 8px;

          ::marker {
            color: ${(p) => p.theme.colors.teal};
          }
        }
      }

      ${SectionSubheadline} {
        margin-bottom: 32px;
      }

      .mButton {
        display: inline-flex;
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 40px;
    flex-direction: column;
    align-items: center;

    .card {
      width: 100%;

      .texts {
        padding: 24px;
        max-width: 100%;

        ${SectionEyebrow} {
          margin-top: 0;
          margin-bottom: 32px;
        }
      }

      .mButton {
        width: 100%;
        text-align: center;
      }
    }
  }
`;

const Section6 = styled(ResponsiveContainer)`
  padding-top: 100px;
  padding-bottom: 100px;

  ${SectionEyebrow}, ${SectionHeadline}, ${SectionSubheadline} {
    width: 100%;
    max-width: 100%;
    text-align: center;
  }

  .row {
    margin: 60px auto;
  }

  .col {
    padding: 15px;
    display: flex;
    flex: 1;
  }

  .card {
    width: 100%;
    border-radius: 8px;
    padding: 32px 24px;
    background: ${(p) => p.theme.colors.slate2};

    .icon {
      height: 50px;
      margin-bottom: 16px;
    }

    .title {
      font-size: 40px;
      margin: 0;
      font-weight: 400;
      line-height: 1.5;

      b {
        color: ${(p) => p.theme.colors.teal};
        font-weight: 400;
      }
    }

    .description {
      margin: 0;
      font-size: 16px;
    }
  }

  .supporting-text {
    margin: 0;
    font-size: 20px;
    text-align: center;
    line-height: 1.5;

    b {
      color: ${(p) => p.theme.colors.yellow};
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 60px;
    padding-bottom: 40px;

    .row {
      margin: 20px auto;
      margin-left: -8px;
      width: calc(100% + 16px);
    }

    .col {
      padding: 8px;
    }

    * {
      text-align: left !important;
    }

    .supporting-text {
      font-size: 18px;
    }

    .card {
      padding: 24px 16px;

      .icon {
        display: none;
      }

      .title {
        font-size: 28px;
        margin-bottom: 8px;
      }

      .description {
        font-size: 14px;
      }
    }
  }
`;

const Section7 = styled.section`
  background: white;
  padding-top: 80px;
  padding-bottom: 80px;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  ${SectionEyebrow} {
    margin-top: 0;
  }

  ${SectionEyebrow}, ${SectionHeadline} {
    text-align: center;
    width: 100%;
    max-width: 100%;
  }

  .accordion {
    width: 100%;
    max-width: 900px;
    margin-top: 40px;
    margin-bottom: 40px;

    .open {
      background: none;
    }

    .content {
      p:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 40px;
    padding-bottom: 40px;

    ${SectionEyebrow}, ${SectionHeadline} {
      text-align: left;
    }

    .mButton {
      display: flex;
      width: 100%;
      text-align: center;
    }

    .accordion {
      margin-top: 0px;

      .item {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
`;

export default PlatformPage;
